// Global Instructions Rule Applied!
//
// Configuration → Pipelines master. Rows are read-only until the row's edit
// (pencil) icon is clicked, which turns that row's cells into inline controls
// (name/type/default-fund/thresholds commit on change, or blur for numbers/
// text); the check icon returns the row to read-only. Create via the modal;
// delete (guarded); the Stages cell opens that pipeline's stages.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined, CheckOutlined, ClockCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import type { PipelinesAdminGridModel } from '../../../state_manager/data_models/pipelines_admin_grid_model.ts'
import { fetchPipelines, createPipeline, updatePipeline, deletePipeline } from '../services/pipelinesConfigService.ts'
import {
  setStageAlertRecipients,
  fetchDigestSchedule,
  saveDigestSchedule,
  DigestSchedule
} from '../../stage_alerts/services/stageAlertsService.ts'
import { refreshBoardConfigs } from '../services/refreshBoardConfigs.ts'
import ColourPickerField from './ColourPickerField.tsx'
import InlineNumberField from './InlineNumberField.tsx'
import InlineTextField from './InlineTextField.tsx'
import PipelineDetailPanel from './PipelineDetailPanel.tsx'

const { Title, Text } = Typography

const token = () => getAccessToken(store.getState().auth)

interface PipelineDraft {
  name: string
  type: string | null
  default: boolean
  default_fund: string | null
  pipeline_warn_days: number | null
  pipeline_warn_colour: string | null
  pipeline_crit_days: number | null
  pipeline_crit_colour: string | null
}

const emptyDraft: PipelineDraft = {
  name: '',
  type: null,
  default: false,
  default_fund: null,
  pipeline_warn_days: null,
  pipeline_warn_colour: null,
  pipeline_crit_days: null,
  pipeline_crit_colour: null
}

function ColourDot({ colour }: { colour: string | null | undefined }): React.ReactElement {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: colour || '#D1D5DB',
        border: '0.5px solid rgba(0,0,0,0.15)',
        verticalAlign: 'middle'
      }}
    />
  )
}

function ThresholdSummary({ days, colour }: { days: number | null | undefined; colour: string | null | undefined }): React.ReactElement {
  if (days == null) return <Text type="secondary">—</Text>
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <ColourDot colour={colour} />
      <span>{`${days}d`}</span>
    </span>
  )
}

export default function PipelinesPanel(): React.ReactElement {
  const [rows, setRows] = useState<PipelinesAdminGridModel[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [detail, setDetail] = useState<{ id: string; name: string } | null>(null)

  // Which row is in inline-edit mode (null = all read-only).
  const [editingId, setEditingId] = useState<string | null>(null)

  // Create modal
  const [modalOpen, setModalOpen] = useState(false)
  const [draft, setDraft] = useState<PipelineDraft>(emptyDraft)
  const [busy, setBusy] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Stage-alert recipients modal (Phase 6): explicit emails per pipeline.
  const [recipientsFor, setRecipientsFor] = useState<{ id: string; name: string; emails: string[] } | null>(null)
  const [recipientsBusy, setRecipientsBusy] = useState(false)

  // Digest schedule modal (Phase 6): cfg-driven send frequency.
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [schedule, setSchedule] = useState<DigestSchedule | null>(null)
  const [scheduleBusy, setScheduleBusy] = useState(false)

  const funds = useSelector(
    (state: { globalConfigs?: { funds?: { id: string; name?: string | null }[] } }) => state.globalConfigs?.funds || []
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const filters = search.trim() ? [{ key: 'pipelines__name', value: search.trim() }] : []
      const result = await fetchPipelines([{ key: 'pipelines__name', direction: 'asc' }], filters, 1, 500, token())
      if (result.error) showErrorToast(result.error)
      setRows(result.data)
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    load()
  }, [load])

  // Inline persist for a single pipeline setting: optimistic local update, then
  // PATCH; on failure refetch (revert), on success refresh the board cache.
  const patchPipeline = useCallback(
    async (row: PipelinesAdminGridModel, aliasField: keyof PipelinesAdminGridModel, columnField: string, value: unknown) => {
      setRows((prev) =>
        prev.map((r) => (r.pipelines__id === row.pipelines__id ? ({ ...r, [aliasField]: value } as PipelinesAdminGridModel) : r))
      )
      const res = await updatePipeline(row.pipelines__id, { [columnField]: value }, token())
      if (!res.success) {
        showErrorToast(res.error || 'Failed to save')
        load()
      } else {
        refreshBoardConfigs()
      }
    },
    [load]
  )

  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>()
    rows.forEach((r) => {
      if (r.pipelines__type) seen.set(r.pipelines__type, r.pipelines__type__label || r.pipelines__type)
    })
    return Array.from(seen, ([value, label]) => ({ value, label }))
  }, [rows])

  const openCreate = useCallback(() => {
    setDraft(emptyDraft)
    setModalOpen(true)
  }, [])

  const submit = useCallback(async () => {
    if (!draft.name.trim()) {
      showErrorToast('Pipeline name is required')
      return
    }
    if (!draft.type) {
      showErrorToast('Pipeline type is required')
      return
    }
    setBusy(true)
    try {
      const result = await createPipeline(
        {
          name: draft.name.trim(),
          type: draft.type,
          default: draft.default,
          default_fund: draft.default_fund,
          pipeline_warn_days: draft.pipeline_warn_days,
          pipeline_warn_colour: draft.pipeline_warn_colour,
          pipeline_crit_days: draft.pipeline_crit_days,
          pipeline_crit_colour: draft.pipeline_crit_colour
        },
        token()
      )
      if (!result.success) {
        showErrorToast(result.error || 'Failed to create pipeline')
        return
      }
      toast.success('Pipeline created')
      setModalOpen(false)
      load()
      refreshBoardConfigs()
    } finally {
      setBusy(false)
    }
  }, [draft, load])

  const saveRecipients = useCallback(async () => {
    if (!recipientsFor) return
    const invalid = recipientsFor.emails.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()))
    if (invalid.length) {
      showErrorToast(`Invalid email(s): ${invalid.join(', ')}`)
      return
    }
    setRecipientsBusy(true)
    try {
      const result = await setStageAlertRecipients(
        recipientsFor.id,
        recipientsFor.emails.map((e) => e.trim().toLowerCase()),
        token()
      )
      if (!result.success) {
        showErrorToast(result.error || 'Failed to save recipients')
        return
      }
      toast.success('Alert recipients saved')
      setRecipientsFor(null)
      load()
    } finally {
      setRecipientsBusy(false)
    }
  }, [recipientsFor, load])

  const openSchedule = useCallback(async () => {
    setScheduleOpen(true)
    setScheduleBusy(true)
    try {
      const result = await fetchDigestSchedule(token())
      if (result.error) {
        showErrorToast(result.error)
        setScheduleOpen(false)
        return
      }
      setSchedule(result.data)
    } finally {
      setScheduleBusy(false)
    }
  }, [])

  const submitSchedule = useCallback(async () => {
    if (!schedule) return
    if (schedule.frequency_unit === 'weekly' && !schedule.weekday) {
      showErrorToast('Pick the weekday the digest should send on')
      return
    }
    if (schedule.frequency_unit === 'monthly' && !schedule.day_of_month) {
      showErrorToast('Pick the day of the month the digest should send on')
      return
    }
    setScheduleBusy(true)
    try {
      const result = await saveDigestSchedule(
        {
          frequency_unit: schedule.frequency_unit,
          interval_count: schedule.interval_count || 1,
          weekday: schedule.frequency_unit === 'weekly' ? schedule.weekday : null,
          day_of_month: schedule.frequency_unit === 'monthly' ? schedule.day_of_month : null,
          enabled: schedule.enabled
        },
        token()
      )
      if (result.error || !result.data) {
        showErrorToast(result.error || 'Failed to save digest schedule')
        return
      }
      setSchedule(result.data)
      toast.success('Digest schedule saved')
      setScheduleOpen(false)
    } finally {
      setScheduleBusy(false)
    }
  }, [schedule])

  const handleDelete = useCallback(
    async (row: PipelinesAdminGridModel) => {
      setDeletingId(row.pipelines__id)
      try {
        const result = await deletePipeline(row.pipelines__id, token())
        if (!result.success) {
          showErrorToast(result.error || 'Failed to delete pipeline')
          return
        }
        toast.success('Pipeline deleted')
        load()
        refreshBoardConfigs()
      } finally {
        setDeletingId(null)
      }
    },
    [load]
  )

  const columns: ColumnsType<PipelinesAdminGridModel> = useMemo(() => {
    const isEditing = (row: PipelinesAdminGridModel): boolean => editingId === row.pipelines__id
    return [
      {
        title: 'Pipeline',
        key: 'pipelines__name',
        width: 190,
        fixed: 'left',
        render: (_: unknown, row) =>
          isEditing(row) ? (
            <InlineTextField
              value={row.pipelines__name || ''}
              placeholder="Pipeline name"
              onCommit={(v) => {
                const name = v.trim()
                if (!name) {
                  showErrorToast('Pipeline name is required')
                  load()
                  return
                }
                patchPipeline(row, 'pipelines__name', 'name', name)
              }}
            />
          ) : (
            <span className="font-medium">{row.pipelines__name || <Text type="secondary">—</Text>}</span>
          )
      },
      {
        title: 'Type',
        key: 'type',
        width: 160,
        render: (_: unknown, row) =>
          isEditing(row) ? (
            <Select
              size="small"
              style={{ width: '100%' }}
              value={row.pipelines__type ?? undefined}
              options={typeOptions}
              onChange={(v) => patchPipeline(row, 'pipelines__type', 'type', v)}
            />
          ) : (
            row.pipelines__type__label || <Text type="secondary">—</Text>
          )
      },
      {
        title: 'Default',
        key: 'default',
        width: 80,
        align: 'center',
        render: (_: unknown, row) =>
          isEditing(row) ? (
            <Switch
              size="small"
              checked={row.pipelines__default ?? false}
              onChange={(v) => patchPipeline(row, 'pipelines__default', 'default', v)}
            />
          ) : row.pipelines__default ? (
            <Tag color="blue">default</Tag>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: 'Default fund',
        key: 'fund',
        width: 160,
        render: (_: unknown, row) =>
          isEditing(row) ? (
            <Select
              size="small"
              allowClear
              placeholder="—"
              style={{ width: '100%' }}
              value={row.pipelines__default_fund ?? undefined}
              options={funds.map((f) => ({ value: f.id, label: f.name || f.id }))}
              onChange={(v) => patchPipeline(row, 'pipelines__default_fund', 'default_fund', v ?? null)}
            />
          ) : (
            row.pipelines__default_fund__label || <Text type="secondary">—</Text>
          )
      },
      {
        title: <Tooltip title="Days in pipeline before the warn colour applies">Warn</Tooltip>,
        key: 'warn',
        width: 120,
        render: (_: unknown, row) =>
          isEditing(row) ? (
            <Space size={4}>
              <InlineNumberField
                value={row.pipelines__pipeline_warn_days ?? null}
                onCommit={(v) => patchPipeline(row, 'pipelines__pipeline_warn_days', 'pipeline_warn_days', v)}
              />
              <ColourPickerField
                value={row.pipelines__pipeline_warn_colour ?? null}
                onChange={(c) => patchPipeline(row, 'pipelines__pipeline_warn_colour', 'pipeline_warn_colour', c)}
              />
            </Space>
          ) : (
            <ThresholdSummary days={row.pipelines__pipeline_warn_days} colour={row.pipelines__pipeline_warn_colour} />
          )
      },
      {
        title: <Tooltip title="Days in pipeline before the critical colour applies">Crit</Tooltip>,
        key: 'crit',
        width: 120,
        render: (_: unknown, row) =>
          isEditing(row) ? (
            <Space size={4}>
              <InlineNumberField
                value={row.pipelines__pipeline_crit_days ?? null}
                onCommit={(v) => patchPipeline(row, 'pipelines__pipeline_crit_days', 'pipeline_crit_days', v)}
              />
              <ColourPickerField
                value={row.pipelines__pipeline_crit_colour ?? null}
                onChange={(c) => patchPipeline(row, 'pipelines__pipeline_crit_colour', 'pipeline_crit_colour', c)}
              />
            </Space>
          ) : (
            <ThresholdSummary days={row.pipelines__pipeline_crit_days} colour={row.pipelines__pipeline_crit_colour} />
          )
      },
      {
        title: (
          <Tooltip title="Stage-aging alert recipients: emails notified (bell + daily digest) when a deal in this pipeline breaches its stage warn/crit thresholds. No recipients = no alerts for this pipeline.">
            Alerts
          </Tooltip>
        ),
        key: 'alert_recipients',
        width: 110,
        align: 'center',
        render: (_: unknown, row) => (
          <Button
            type="link"
            size="small"
            className="!px-0"
            onClick={() =>
              setRecipientsFor({
                id: row.pipelines__id,
                name: row.pipelines__name || '',
                emails: (row.stage_alert_recipients || []).map((r) => r.stage_alert_recipients__email)
              })
            }
          >
            {row.pipelines__alert_recipient_count
              ? `${row.pipelines__alert_recipient_count} recipient${row.pipelines__alert_recipient_count === 1 ? '' : 's'}`
              : 'None'}
          </Button>
        )
      },
      {
        title: 'Stages',
        key: 'stages',
        width: 90,
        align: 'center',
        render: (_: unknown, row) => (
          <Button
            type="link"
            size="small"
            className="!px-0"
            onClick={() => setDetail({ id: row.pipelines__id, name: row.pipelines__name || '' })}
          >
            {row.pipelines__stage_count ?? 0} stages
          </Button>
        )
      },
      {
        title: 'Deals',
        dataIndex: 'pipelines__deal_count',
        key: 'deals',
        width: 70,
        align: 'right',
        render: (n: number | null) => <Text type={n ? undefined : 'secondary'}>{n ?? 0}</Text>
      },
      {
        title: '',
        key: 'actions',
        width: 80,
        align: 'center',
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size="small">
            {isEditing(row) ? (
              <Tooltip title="Done editing">
                <Button type="text" size="small" icon={<CheckOutlined />} onClick={() => setEditingId(null)} />
              </Tooltip>
            ) : (
              <Tooltip title="Edit">
                <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditingId(row.pipelines__id)} />
              </Tooltip>
            )}
            <Popconfirm
              title="Delete this pipeline?"
              description="Only empty pipelines (no stages or deals) can be deleted."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(row)}
            >
              <Tooltip title="Delete">
                <Button type="text" size="small" danger loading={deletingId === row.pipelines__id} icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      }
    ]
  }, [editingId, typeOptions, funds, patchPipeline, handleDelete, deletingId])

  if (detail) {
    return (
      <PipelineDetailPanel
        pipelineId={detail.id}
        pipelineName={detail.name}
        onBack={() => {
          setDetail(null)
          load()
        }}
      />
    )
  }

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <Title level={4} style={{ margin: 0 }}>
          Pipelines
        </Title>
        <Space>
          <Input.Search placeholder="Search pipelines..." allowClear onSearch={(v) => setSearch(v || '')} style={{ width: 240 }} />
          <Button icon={<ClockCircleOutlined />} onClick={openSchedule}>
            Digest schedule
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New pipeline
          </Button>
        </Space>
      </div>

      <div className="flex-1 min-h-0">
        <Table<PipelinesAdminGridModel>
          rowKey="pipelines__id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          size="middle"
          scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
        />
      </div>

      <Modal
        title="New pipeline"
        open={modalOpen}
        okText="Create"
        confirmLoading={busy}
        onOk={submit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <div className="flex flex-col gap-3">
          <Input placeholder="Pipeline name" value={draft.name} maxLength={120} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <Select
            placeholder="Pipeline type"
            style={{ width: '100%' }}
            value={draft.type || undefined}
            onChange={(v) => setDraft({ ...draft, type: v })}
            options={typeOptions}
          />
          <div className="flex items-center justify-between">
            <Text>Default pipeline</Text>
            <Switch checked={draft.default} onChange={(v) => setDraft({ ...draft, default: v })} />
          </div>
          <Select
            allowClear
            placeholder="Default fund (optional)"
            style={{ width: '100%' }}
            value={draft.default_fund || undefined}
            onChange={(v) => setDraft({ ...draft, default_fund: v ?? null })}
            options={funds.map((f) => ({ value: f.id, label: f.name || f.id }))}
          />

          <Text strong style={{ marginTop: 4 }}>
            Days-in-pipeline colour bands
          </Text>
          <div className="flex items-center justify-between">
            <Text>Warn after (days)</Text>
            <Space>
              <InputNumber
                min={0}
                placeholder="—"
                value={draft.pipeline_warn_days ?? undefined}
                onChange={(v) => setDraft({ ...draft, pipeline_warn_days: typeof v === 'number' ? v : null })}
              />
              <ColourPickerField value={draft.pipeline_warn_colour} onChange={(c) => setDraft({ ...draft, pipeline_warn_colour: c })} />
            </Space>
          </div>
          <div className="flex items-center justify-between">
            <Text>Critical after (days)</Text>
            <Space>
              <InputNumber
                min={0}
                placeholder="—"
                value={draft.pipeline_crit_days ?? undefined}
                onChange={(v) => setDraft({ ...draft, pipeline_crit_days: typeof v === 'number' ? v : null })}
              />
              <ColourPickerField value={draft.pipeline_crit_colour} onChange={(c) => setDraft({ ...draft, pipeline_crit_colour: c })} />
            </Space>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Leave a day value blank to disable that band. Applies to every deal's time in this pipeline.
          </Text>
        </div>
      </Modal>

      {/* Digest schedule modal (Phase 6) */}
      <Modal
        title="Stage-alert digest schedule"
        open={scheduleOpen}
        okText="Save"
        confirmLoading={scheduleBusy}
        onOk={submitSchedule}
        onCancel={() => setScheduleOpen(false)}
        destroyOnClose
      >
        {schedule && (
          <div className="flex flex-col gap-3">
            <Text>
              How often the stage-alert digest email is sent to each pipeline's recipients. It only ever
              sends when at least one deal is past its stage thresholds.
            </Text>
            <div className="flex items-center justify-between">
              <Text>Digest enabled</Text>
              <Switch
                checked={schedule.enabled}
                onChange={(v) => setSchedule({ ...schedule, enabled: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Text>Frequency</Text>
              <Space>
                <span>Every</span>
                <InputNumber
                  min={1}
                  max={99}
                  style={{ width: 64 }}
                  value={schedule.interval_count}
                  onChange={(v) => setSchedule({ ...schedule, interval_count: typeof v === 'number' ? v : 1 })}
                />
                <Select
                  style={{ width: 110 }}
                  value={schedule.frequency_unit}
                  onChange={(v) =>
                    setSchedule({
                      ...schedule,
                      frequency_unit: v,
                      weekday: v === 'weekly' ? schedule.weekday || 1 : null,
                      day_of_month: v === 'monthly' ? schedule.day_of_month || 1 : null
                    })
                  }
                  options={[
                    { value: 'daily', label: schedule.interval_count === 1 ? 'day' : 'days' },
                    { value: 'weekly', label: schedule.interval_count === 1 ? 'week' : 'weeks' },
                    { value: 'monthly', label: schedule.interval_count === 1 ? 'month' : 'months' }
                  ]}
                />
              </Space>
            </div>
            {schedule.frequency_unit === 'weekly' && (
              <div className="flex items-center justify-between">
                <Text>On weekday</Text>
                <Select
                  style={{ width: 140 }}
                  value={schedule.weekday ?? 1}
                  onChange={(v) => setSchedule({ ...schedule, weekday: v })}
                  options={[
                    { value: 1, label: 'Monday' },
                    { value: 2, label: 'Tuesday' },
                    { value: 3, label: 'Wednesday' },
                    { value: 4, label: 'Thursday' },
                    { value: 5, label: 'Friday' },
                    { value: 6, label: 'Saturday' },
                    { value: 7, label: 'Sunday' }
                  ]}
                />
              </div>
            )}
            {schedule.frequency_unit === 'monthly' && (
              <div className="flex items-center justify-between">
                <Text>On day of month</Text>
                <InputNumber
                  min={1}
                  max={31}
                  style={{ width: 80 }}
                  value={schedule.day_of_month ?? 1}
                  onChange={(v) => setSchedule({ ...schedule, day_of_month: typeof v === 'number' ? v : 1 })}
                />
              </div>
            )}
            {schedule.frequency_unit === 'monthly' && (schedule.day_of_month ?? 1) > 28 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Months without day {schedule.day_of_month} send on their last day.
              </Text>
            )}
            {schedule.enabled && schedule.next_send_dates.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Next sends (current saved schedule):{' '}
                {schedule.next_send_dates.map((d) => dayjs(d).format('MM/DD/YYYY')).join(', ')}. Saving
                restarts every-N cycles from today.
              </Text>
            )}
          </div>
        )}
      </Modal>

      {/* Stage-alert recipients modal (Phase 6) */}
      <Modal
        title={`Stage alert recipients — ${recipientsFor?.name || ''}`}
        open={recipientsFor !== null}
        okText="Save"
        confirmLoading={recipientsBusy}
        onOk={saveRecipients}
        onCancel={() => setRecipientsFor(null)}
        destroyOnClose
      >
        <div className="flex flex-col gap-3">
          <Text>
            These emails receive stage-aging alerts for this pipeline: the in-app bell and the digest email
            (per the configured schedule, only when at least one deal is past its stage thresholds).
          </Text>
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Add email addresses…"
            tokenSeparators={[',', ' ', ';']}
            value={recipientsFor?.emails || []}
            onChange={(v) => setRecipientsFor((prev) => (prev ? { ...prev, emails: v as string[] } : prev))}
            open={false}
            suffixIcon={null}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Removing every recipient disables stage alerts for this pipeline. Thresholds are configured per
            stage in the pipeline's Stages panel.
          </Text>
        </div>
      </Modal>
    </div>
  )
}
